import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import WLogo from "../assets/TravelXOXO 1.png";
import DLogo from "../assets/TravelXOXO 2.png";

import {
  PhoneIcon,
  ShieldCheckIcon,
  CubeIcon,
  MapPinIcon,
  QrCodeIcon,
  ComputerDesktopIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

export default function Home() {
  const [theme, setTheme] = useState("dark");

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
  }, [theme]);

  return (
    <div className="bg-gray-100 text-black dark:bg-black dark:text-white min-h-screen">

      {/* HEADER */}
      <header className="bg-white text-black dark:bg-black dark:text-white flex items-center justify-between px-16 py-4 border-b border-black/10 dark:border-white/10">
        <img src={WLogo} className="h-8 dark:hidden block" alt="Travelxoxo Logo" />
        <img src={DLogo} className="h-8 hidden dark:block" alt="Travelxoxo Logo" />

        <nav className="hidden md:flex gap-6 text-black/70 dark:text-white/70 text-sm">
          <a href="#why" className="hover:text-black dark:hover:text-white">Why Tags</a>
          <a href="#workflow" className="hover:text-black dark:hover:text-white">How it works</a>
          <a href="#start" className="hover:text-black dark:hover:text-white">Get Started</a>
          <Link to="/requestform" className="hover:text-black dark:hover:text-white">Request Stickers</Link>
        </nav>

        <button
          onClick={toggleTheme}
          className="w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center hover:scale-105 transition"
        >
          {theme === "dark" ? "🌞" : "🌙"}
        </button>
      </header>

      {/* HERO */}
      <section className="px-16 py-20 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold">
          Never Lose Your Belongings Again
        </h1>
        <p className="mt-4 text-lg text-black/60 dark:text-white/70">
          Smart QR tags that help reunite you with your lost items instantly
        </p>

        <button
          onClick={() =>
            document.getElementById("workflow").scrollIntoView({ behavior: "smooth" })
          }
          className="mt-8 px-6 py-3 bg-orange-600 hover:bg-orange-700 rounded-xl text-white font-semibold"
        >
          See How It Works
        </button>
      </section>

      {/* WHY */}
      <section id="why" className="px-16 py-16">
        <h2 className="text-3xl font-extrabold text-center mb-10">
          Why TravelXOXO Tags?
        </h2>

        <div className="grid md:grid-cols-3 gap-6">

          <div className="p-6 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/5">
            <PhoneIcon className="h-10 w-10 text-orange-500" />
            <h4 className="mt-4 font-bold text-lg">Instant Contact</h4>
            <p className="mt-2 text-black/70 dark:text-white/70">
              People reach you instantly via call or WhatsApp.
            </p>
          </div>

          <div className="p-6 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/5">
            <ShieldCheckIcon className="h-10 w-10 text-orange-500" />
            <h4 className="mt-4 font-bold text-lg">Privacy Protected</h4>
            <p className="mt-2 text-black/70 dark:text-white/70">
              Your details only show when your tag is scanned.
            </p>
          </div>

          <div className="p-6 rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-white/5">
            <CubeIcon className="h-10 w-10 text-orange-500" />
            <h4 className="mt-4 font-bold text-lg">Multiple Items</h4>
            <p className="mt-2 text-black/70 dark:text-white/70">
              Suitable for bags, laptops, keys, bottles and more.
            </p>
          </div>

        </div>
      </section>

      {/* WORKFLOW */}
      

<section id="workflow" className="px-10 py-20">
  <h2 className="text-3xl font-extrabold text-center mb-10">
    Contact Workflow
  </h2>

  <div className="max-w-3xl mx-auto">

    {[
      { icon: MapPinIcon, title: "Item Found", text: "Someone discovers your lost item with the TravelXOXO tag attached." },
      { icon: QrCodeIcon, title: "Scan QR Code", text: "Finder uses their smartphone camera to scan the unique QR code on the tag." },
      { icon: ComputerDesktopIcon, title: "View Contact Page", text: "Secure webpage opens displaying your name, organization, contact numbers, and return instructions." },
      { icon: ChatBubbleLeftRightIcon, title: "Direct Contact", text: "Finder clicks to call your phone or message via WhatsApp instantly." },
      { icon: CheckCircleIcon, title: "Reunion Complete", text: "Coordinate pickup location and time to recover your belongings safely." }
    ].map(({ icon: Icon, title, text }, i, arr) => (
      <React.Fragment key={i}>

        {/* BOX */}
        <div className="p-6 bg-white dark:bg-white/5 rounded-xl border border-gray-300 dark:border-gray-700 flex gap-2">
          <Icon className="h-10 w-10 text-orange-500" />
          <div>
            <h4 className="font-bold">{title}</h4>
            <p className="text-gray-700 dark:text-white/70">{text}</p>
          </div>
        </div>

        {/* ARROW (all except last item) */}
        {i < arr.length - 1 && (
          <div className="flex justify-center">
            <span className="text-orange-500 text-3xl">↓</span>
          </div>
        )}

      </React.Fragment>
    ))}

  </div>
</section>

      {/* GETTING STARTED */}
      <section id="start" className="px-16 py-20 text-center border-t border-black/10 dark:border-white/10">
        <h2 className="text-3xl font-extrabold mb-8">Getting Started</h2>
        <p className="text-black/70 dark:text-white/70 mb-10">Three simple steps</p>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            "Order Tags",
            "Attach to Items",
            "Stay Protected"
          ].map((step, i) => (
            <div key={i}>
              <div className="h-12 w-12 bg-orange-600 rounded-full flex items-center justify-center mx-auto font-bold">
                {i + 1}
              </div>
              <h4 className="font-bold mt-4">{step}</h4>
            </div>
          ))}
        </div>
      </section>

      <footer className="bg-white dark:bg-black border-t border-black/10 dark:border-white/10 py-8">
  <div className="max-w-5xl mx-auto px-6 md:px-16 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-black/60 dark:text-white/60">

    <div>
      © {new Date().getFullYear()} TravelXOXO. All rights reserved.
    </div>

    <div className="flex gap-6">
      <Link
        to="/privacypolicy"
        className="hover:text-black dark:hover:text-white transition"
      >
        Privacy Policy
      </Link>
      <Link
        to="/terms"
        className="hover:text-black dark:hover:text-white transition"
      >
        Terms of Use
      </Link>
    </div>

  </div>
</footer>


    </div>
  );
}

